import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RolesService } from '../../services/roles.service';
import { MenuPermissionDto } from '@app/api/user-service';

type PermissionApp = 'back_office' | 'pos';

interface PermissionTab {
  app: PermissionApp;
  label: string;
}

type MenuState = Record<PermissionApp, MenuGroup[]>;

const PERMISSION_TABS: PermissionTab[] = [
  { app: 'back_office', label: 'BO' },
  { app: 'pos', label: 'POS' },
];

/** API may still return these; omit from role permission tree until backend drops them. */
const SUBMENUS_HIDDEN_FROM_ROLE_UI = new Set(['channel_warehouse']);

/** Standard sales orders: edit/delete not offered on BO (RBA-1600). Hide even if OPA lags. */
function isStandardSalesOrderEditOrDelete(item: MenuPermissionDto): boolean {
  return (
    item.menu === 'orders' &&
    item.submenu === 'standard_sales_orders' &&
    (item.action === 'edit' || item.action === 'delete')
  );
}
const POS_PACKAGE_OPERATION_SUBMENUS = new Set([
  'package_operation',
  'package_sku_operations',
]);

// submenu="spu" and submenu="product" both mean SPU on the backend; merge into one node.
function normalizeProductSpuSubmenu(item: MenuPermissionDto): MenuPermissionDto {
  return item.menu === 'product' && item.submenu === 'spu'
    ? { ...item, submenu: 'product' }
    : item;
}

const MENU_LABEL_KEYS: Record<string, string> = {
  channel: 'configuration.roles.permissions.main_nodes.channel',
  pos: 'configuration.roles.permissions.main_nodes.pos',
  product: 'configuration.roles.permissions.main_nodes.product',
  inventory: 'configuration.roles.permissions.main_nodes.inventory',
  procurement: 'configuration.roles.permissions.main_nodes.procurement',
  distribution: 'configuration.roles.permissions.main_nodes.distribution',
  'third-party': 'configuration.roles.permissions.main_nodes.third_party',
  orders: 'configuration.roles.permissions.main_nodes.orders',
  o2o_orders: 'configuration.roles.permissions.main_nodes.o2o_orders',
  warehouse: 'configuration.roles.permissions.main_nodes.warehouse',
  'after-sales': 'configuration.roles.permissions.main_nodes.after_sales',
  marketing: 'configuration.roles.permissions.main_nodes.marketing',
  finance: 'configuration.roles.permissions.main_nodes.finance',
  report: 'configuration.roles.permissions.main_nodes.report',
  settings: 'configuration.roles.permissions.main_nodes.settings',
  'e-invoice': 'configuration.roles.permissions.main_nodes.e_invoice',
  commerce_crm: 'sidebar.commerce.crm',
  commerce_member: 'sidebar.commerce.member',
  commerce_hrm: 'sidebar.commerce.hrm',
  commerce_bpm: 'sidebar.commerce.bpm',
  commerce_platform: 'sidebar.commerce.platform',
};

const SUBMENU_LABEL_KEYS: Record<string, string> = {
  // POS child nodes
  point_of_sale: 'configuration.roles.permissions.submenu.point_of_sale',
  open_shift: 'configuration.roles.permissions.submenu.open_shift',
  close_shift: 'configuration.roles.permissions.submenu.close_shift',
  cash_management: 'configuration.roles.permissions.submenu.cash_management',
  product_management:
    'configuration.roles.permissions.submenu.product_management',
  order_management: 'configuration.roles.permissions.submenu.order_management',
  customer_management:
    'configuration.roles.permissions.submenu.customer_management',
  discount_management:
    'configuration.roles.permissions.submenu.discount_management',
  staff_management: 'configuration.roles.permissions.submenu.staff_management',
  payment_methods: 'configuration.roles.permissions.submenu.payment_methods',
  system_logs: 'configuration.roles.permissions.submenu.system_logs',
  device_activation: 'configuration.roles.permissions.submenu.device_activation',
  online_store: 'sidebar.business_center.online_store_management',
  retail_store: 'sidebar.business_center.retail_store_management',
  company: 'sidebar.setting.company_management',
  pos_profile: 'sidebar.business_center.pos_profile',
  open_pos_shift: 'sidebar.business_center.pos_shift_opening_tracking',
  close_pos_shift: 'sidebar.business_center.pos_shift_closing_tracking',
  product: 'sidebar.business_center.product_management',
  sku: 'sidebar.business_center.sku_management',
  category: 'sidebar.business_center.category_management',
  brand: 'sidebar.business_center.brand_management',
  product_asset: 'sidebar.business_center.product_asset_management',
  product_pricing: 'sidebar.business_center.product_pricing',
  price_history: 'sidebar.business_center.price_history',
  product_review_approval: 'sidebar.business_center.product_review_approval',
  sync_report: 'configuration.roles.permissions.submenu.sync_report',
  inventory_lookup: 'sidebar.business_center.inventory_lookup',
  inventory_change_lookup: 'sidebar.business_center.inventory_change_lookup',
  barcode_correction_log: 'sidebar.business_center.barcode_correction_log',
  inbound: 'sidebar.business_center.inbound_management',
  outbound: 'sidebar.business_center.outbound_management',
  order_printing_shipping: 'sidebar.business_center.order_printing_shipping',
  warehouse_transfer: 'sidebar.business_center.warehouse_transfer',
  reserved_inventory: 'sidebar.business_center.reserved_inventory_management',
  batch: 'sidebar.business_center.batch_management',
  stock_alert: 'sidebar.business_center.stock_alert',
  inventory_change_approval:
    'sidebar.business_center.inventory_change_approval',
  virtual_warehouse_allocation:
    'sidebar.business_center.virtual_warehouse_allocation',
  opening_inventory: 'sidebar.business_center.initial_inventory_adjustment',
  inventory_adjustment: 'sidebar.business_center.stock_synchronization',
  putaway: 'configuration.roles.permissions.submenu.putaway',
  picking: 'configuration.roles.permissions.submenu.picking',
  package_operation: 'sidebar.business_center.package_sku_operations',
  package_sku_operations:
    'sidebar.business_center.package_sku_operations',
  purchase_request: 'sidebar.business_center.purchase_request_management',
  purchase_order: 'sidebar.business_center.purchase_order_management',
  vendor: 'sidebar.business_center.supplier_management',
  vendor_products: 'sidebar.business_center.supplier_products',
  purchase_inbound_order: 'sidebar.business_center.purchase_inbound_order',
  purchase_return_order: 'sidebar.business_center.purchase_return_order',
  sales_request: 'sidebar.business_center.sales_request_management',
  sales_order: 'sidebar.business_center.sales_order_management',
  stuck_alerts: 'sidebar.business_center.stuck_stock_hold_alerts',
  flash_sale: 'sidebar.business_center.flash_sale_management',
  'price.flash_sale_quota': 'sidebar.setting.flash_sale_quota',
  'third-party_products': 'sidebar.business_center.third_party_products',
  'third-party_sales_orders':
    'sidebar.business_center.third_party_sales_orders',
  'third-party_o2o_orders': 'sidebar.business_center.third_party_o2o_orders',
  'third-party_after-sales_orders':
    'sidebar.business_center.third_party_after_sales_orders',
  standard_sales_orders: 'sidebar.business_center.internal_sales_orders',
  ecommerce_orders: 'sidebar.business_center.ecommerce_orders',
  pending_sales_orders: 'sidebar.business_center.pending_sales_orders',
  'pre-allocation': 'sidebar.business_center.pre_allocation_management',
  sourcing: 'sidebar.business_center.sourcing_management',
  order_stockout: 'sidebar.business_center.order_stockout_management',
  o2o_orders: 'sidebar.business_center.internal_o2o_orders',
  pending_o2o_orders: 'sidebar.business_center.pending_o2o_orders',
  order_change_history: 'sidebar.business_center.o2o_change_history',
  logical_warehouse: 'sidebar.business_center.logical_warehouse_management',
  virtual_warehouse: 'sidebar.business_center.virtual_warehouse_management',
  virtual: 'sidebar.business_center.virtual_warehouse_page',
  'after-sales_orders': 'sidebar.business_center.internal_after_sales_orders',
  'pending_after-sales_orders':
    'sidebar.business_center.pending_after_sales_orders',
  promotion: 'sidebar.business_center.promotion_management',
  coupon: 'sidebar.business_center.coupon_management',
  payment_history: 'sidebar.business_center.payment_history',
  marketplace_bills_raw: 'sidebar.business_center.platform_bill_original',
  marketplace_billing: 'sidebar.business_center.platform_bill_management',
  marketplace_reconciliation:
    'sidebar.business_center.platform_reconciliation_management',
  channel_reports: 'sidebar.business_center.channel_reports',
  point_of_sale_reports: 'sidebar.business_center.pos_reports',
  product_reports: 'sidebar.business_center.product_reports',
  inventory_reports: 'sidebar.business_center.inventory_reports',
  procurement_reports: 'sidebar.business_center.procurement_reports',
  'third-party_reports': 'sidebar.business_center.third_party_reports',
  orders_reports: 'sidebar.business_center.orders_reports',
  o2o_orders_reports: 'sidebar.business_center.o2o_orders_reports',
  warehouse_reports: 'sidebar.business_center.warehouse_reports',
  'after-sales_reports': 'sidebar.business_center.after_sales_reports',
  marketing_reports: 'sidebar.business_center.marketing_reports',
  finance_reports: 'sidebar.business_center.finance_reports',
  sales_reports: 'sidebar.business_center.sales_reports',
  'user.employee_management': 'sidebar.setting.employee_management',
  'user.roles_and_permissions_management':
    'sidebar.setting.role_permission_management',
  'user.business_unit': 'sidebar.setting.business_unit_management',
  'user.roles_assignment': 'sidebar.setting.user_roles_assignment',
  'price.selling_tabs': 'sidebar.setting.price',
  'product.floor_margin': 'sidebar.setting.floor_margin',
  'product.product_approval_policy': 'sidebar.setting.product_approval_policy',
  'product.serial_profile': 'sidebar.setting.serial_profile',
  'product.tax_categories': 'sidebar.setting.tax_categories',
  'inventory.warehouse_structure_configuration':
    'sidebar.setting.warehouse_structure_configuration',
  'inventory.inventory_ratio_configuration':
    'sidebar.setting.inventory_ratio_configuration',
  'inventory.inventory_approval_policy':
    'sidebar.setting.inventory_approval_policy',
  'inventory.virtual_warehouse_stock_rules':
    'sidebar.setting.virtual_warehouse_stock_rules',
  'inventory.alert_settings': 'sidebar.setting.alert_settings',
  'inventory.stock_hold_timeout': 'sidebar.setting.stock_hold_timeout',
  'inventory.return_expiry_threshold': 'sidebar.setting.return_expiry_threshold',
  'return_exception': 'sidebar.business_center.return_exception',
  'logistics.carrier_management': 'sidebar.setting.carrier_management',
  'logistics.logistics_policy': 'sidebar.setting.logistics_policy',
  'logistics.logistics_authorization':
    'sidebar.setting.logistics_authorization',
  'procurement.purchase_order_configuration':
    'sidebar.setting.purchase_order_configuration',
  'order.sourcing_configuration': 'sidebar.setting.sourcing_configuration',
  'order.sourcing_policy': 'sidebar.setting.sourcing_policy',
  'order.system_order_labels': 'sidebar.setting.system_order_labels',
  'order.manual_order_labels': 'sidebar.setting.manual_order_labels',
  'order.after_sales_reasons': 'sidebar.setting.after_sales_reasons',
  'order.delivery_retry': 'sidebar.setting.delivery_retry',
  'order.modification_approval_timeout':
    'sidebar.setting.modification_approval_timeout',
  'order.modification_approval_threshold':
    'sidebar.setting.modification_approval_threshold',
  'order.cash_payment_max_amount': 'sidebar.setting.cash_payment_max_amount',
  'finance.payment_configuration': 'sidebar.setting.payment_configuration',
  'finance.platform_billing_parsing_policy':
    'sidebar.setting.platform_billing_parsing_policy',
  'master_data.transaction_list': 'sidebar.setting.transaction_list',
  'master_data.workflow_design': 'sidebar.setting.workflow_design',
  'master_data.system_applications': 'sidebar.setting.system_applications',
  'master_data.coding_rules': 'sidebar.setting.coding_rules',
  'master_data.value_set_configuration':
    'sidebar.setting.value_set_configuration',
  'master_data.value_set_view': 'sidebar.setting.value_set_view',
  'master_data.label_management': 'sidebar.setting.label_management',
  'master_data.external_store_mapping':
    'sidebar.setting.external_store_mapping',
  'import.import_history': 'sidebar.setting.import_history',
  'import.general_import_management':
    'sidebar.setting.general_import_management',
  'file.summary_query': 'sidebar.setting.summary_query',
  'file.storage_configuration': 'sidebar.setting.storage_configuration',
  'file.upload_capacity_configuration':
    'sidebar.setting.upload_capacity_configuration',
  'file.upload_detail_configuration':
    'sidebar.setting.upload_detail_configuration',
  e_invoice_provider: 'sidebar.setting.e_invoice_provider',
  e_invoice_account: 'sidebar.setting.e_invoice_account',
  e_invoice_history: 'sidebar.setting.e_invoice_history',
  // Commerce screens reuse their page titles. Scoped keys disambiguate
  // submenu names shared by more than one menu.
  'pos.settings': 'configuration.roles.permissions.submenu.pos_settings',
  'commerce_member.settings': 'commerce.member.settings.title',
  'commerce_crm.performance': 'commerce.crm.performance.title',
  'commerce_hrm.performance': 'commerce.hrm.performance.title',
  customers: 'commerce.crm.customers.title',
  customer_pool: 'commerce.crm.customer_pool.title',
  contacts: 'commerce.crm.contacts.title',
  leads: 'commerce.crm.leads.title',
  opportunities: 'commerce.crm.opportunities.title',
  contracts: 'commerce.crm.contracts.title',
  contract_approval: 'commerce.crm.contract_approval.title',
  products: 'commerce.crm.products.title',
  receivables: 'commerce.crm.receivables.title',
  receivable_plans: 'commerce.crm.receivable_plans.title',
  follow_ups: 'commerce.crm.follow_ups.title',
  statistics: 'commerce.crm.statistics.title',
  list: 'commerce.member.list.title',
  tags: 'commerce.member.tags.title',
  groups: 'commerce.member.groups.title',
  levels: 'commerce.member.levels.title',
  points: 'commerce.member.points.title',
  addresses: 'commerce.member.addresses.title',
  employees: 'commerce.hrm.employees.title',
  org: 'commerce.hrm.org.title',
  attendance: 'commerce.hrm.attendance.title',
  clock_groups: 'commerce.hrm.clock_groups.title',
  shifts: 'commerce.hrm.shifts.title',
  leaves: 'commerce.hrm.leaves.title',
  leave_calendar: 'commerce.hrm.leave_calendar.title',
  holidays: 'commerce.hrm.holidays.title',
  payroll: 'commerce.hrm.payroll.title',
  payslips: 'commerce.hrm.payslips.title',
  insurance: 'commerce.hrm.insurance.title',
  recruitment: 'commerce.hrm.recruitment.title',
  portal: 'commerce.hrm.portal.title',
  inbox: 'commerce.bpm.inbox.title',
  started: 'commerce.bpm.started.title',
  catalog: 'commerce.bpm.catalog.title',
  forms: 'commerce.bpm.forms.title',
  models: 'commerce.bpm.models.title',
  definitions: 'commerce.bpm.definitions.title',
  instances: 'commerce.bpm.instances.title',
  listeners: 'commerce.bpm.listeners.title',
  user_groups: 'commerce.bpm.user_groups.title',
  modules: 'commerce.platform.modules.title',
};

const ACTION_LABEL_KEYS: Record<string, string> = {
  view: 'configuration.roles.permissions.action_labels.view',
  create: 'configuration.roles.permissions.action_labels.create',
  edit: 'configuration.roles.permissions.action_labels.edit',
  delete: 'configuration.roles.permissions.action_labels.delete',
  approve: 'configuration.roles.permissions.action_labels.approve',
  approve_l1: 'configuration.roles.permissions.action_labels.approve_l1',
  approve_l2: 'configuration.roles.permissions.action_labels.approve_l2',
  confirm: 'configuration.roles.permissions.action_labels.confirm',
  cancel: 'configuration.roles.permissions.action_labels.cancel',
  receive: 'configuration.roles.permissions.action_labels.receive',
  view_history: 'configuration.roles.permissions.action_labels.view_history',
  pack: 'configuration.roles.permissions.action_labels.pack',
  unpack: 'configuration.roles.permissions.action_labels.unpack',
  deactivate: 'configuration.roles.permissions.action_labels.deactivate',
  send: 'configuration.roles.permissions.action_labels.send',
  confirm_vendor: 'configuration.roles.permissions.action_labels.confirm_vendor',
  deliver: 'configuration.roles.permissions.action_labels.deliver',
  done: 'configuration.roles.permissions.action_labels.done',
  close: 'configuration.roles.permissions.action_labels.close',
  reject: 'configuration.roles.permissions.action_labels.reject',
  release: 'configuration.roles.permissions.action_labels.release',
  edit_pricing: 'configuration.roles.permissions.action_labels.edit_pricing',
  edit_marketing_content:
    'configuration.roles.permissions.action_labels.edit_marketing_content',
  edit_barcode: 'configuration.roles.permissions.action_labels.edit_barcode',
  edit_package_composition:
    'configuration.roles.permissions.action_labels.edit_package_composition',
  edit_supplier:
    'configuration.roles.permissions.action_labels.edit_supplier',
  edit_base_price:
    'configuration.roles.permissions.action_labels.edit_base_price',
  approve_mod_l1:
    'configuration.roles.permissions.action_labels.approve_mod_l1',
  approve_mod_l2:
    'configuration.roles.permissions.action_labels.approve_mod_l2',
  approve_mod_l3:
    'configuration.roles.permissions.action_labels.approve_mod_l3',
};

export interface ActionPermission {
  action: string;
  code: string;
  checked: boolean;
  route: string;
}

export interface SubmenuPermission {
  name: string;
  route: string | null;
  actions: ActionPermission[];
  expanded: boolean;
}

export interface MenuGroup {
  name: string;
  submenus: SubmenuPermission[];
  expanded: boolean;
}

@Component({
  selector: 'app-permission-tree',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxModule, TranslateModule],
  templateUrl: './permission-tree.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermissionTreeComponent {
  private readonly rolesService = inject(RolesService);
  private readonly translateService = inject(TranslateService);
  private loadingCount = 0;

  readonly permissionTabs = PERMISSION_TABS;
  menusByApp = signal<MenuState>({
    back_office: [],
    pos: [],
  });
  loading = signal(false);
  activeApp = signal<PermissionApp>('back_office');
  searchTerm = input('');
  readOnly = input(false);
  selectedMenuCodes = input<Set<string> | string[] | null>([]);

  @Output() savePermissions = new EventEmitter<string[]>();
  visibleMenus = computed(() => {
    const term = this.normalizedSearchTerm();
    const menus = this.currentMenus();

    if (!term) {
      return menus;
    }

    return this.filterMenus(menus, term);
  });

  constructor() {
    this.loadAllPermissions();

    effect(() => {
      const menuCodes = this.toMenuCodeSet(this.selectedMenuCodes());
      const currentMenus = untracked(() => this.menusByApp());
      const updatedMenus = this.applyCheckedStateByApp(currentMenus, menuCodes);
      const checkedCount = Object.values(updatedMenus)
        .flat()
        .flatMap((menu) => menu.submenus)
        .flatMap((sub) => sub.actions)
        .filter((action) => action.checked).length;

      this.menusByApp.set(updatedMenus);
    });
  }

  selectTab(app: PermissionApp): void {
    if (this.activeApp() === app) {
      return;
    }

    this.activeApp.set(app);
  }

  isActiveTab(app: PermissionApp): boolean {
    return this.activeApp() === app;
  }

  getMenuLabel(menuName: string): string {
    const key = MENU_LABEL_KEYS[menuName];

    if (!key) {
      return menuName;
    }

    const translated = this.translateService.instant(key);
    return translated !== key ? translated : menuName;
  }

  getSubmenuLabel(submenuName: string, menuName?: string): string {
    const key =
      (menuName ? SUBMENU_LABEL_KEYS[`${menuName}.${submenuName}`] : undefined) ??
      SUBMENU_LABEL_KEYS[submenuName];

    if (!key) {
      return submenuName;
    }

    const translated = this.translateService.instant(key);
    return translated !== key ? translated : submenuName;
  }

  getActionLabel(actionName: string): string {
    const key = ACTION_LABEL_KEYS[actionName.toLowerCase()];

    if (!key) {
      return actionName;
    }

    const translated = this.translateService.instant(key);
    return translated !== key ? translated : actionName;
  }

  private currentMenus(): MenuGroup[] {
    return this.menusByApp()[this.activeApp()];
  }

  private loadAllPermissions(): void {
    this.permissionTabs.forEach((tab) => this.loadPermissions(tab.app));
  }

  private loadPermissions(app: PermissionApp): void {
    this.startLoading();
    this.rolesService.getPermissions(app).subscribe({
      next: (response) => {
        this.setData(app, response.menuPermissions || []);
        this.stopLoading();
      },
      error: () => {
        this.setData(app, []);
        this.stopLoading();
      },
    });
  }

  setData(app: PermissionApp, data: MenuPermissionDto[]) {
    const filtered = data
      .filter(
        (item) =>
          !SUBMENUS_HIDDEN_FROM_ROLE_UI.has(
            (item.submenu || '').toLowerCase(),
          ) && !isStandardSalesOrderEditOrDelete(item),
      )
      .map(normalizeProductSpuSubmenu);
    const tree = this.buildMenuTree(
      this.assignPosPackageOperationMenu(app, filtered),
    );
    const menuCodes = this.toMenuCodeSet(this.selectedMenuCodes());
    const treeCodes = tree
      .flatMap((menu) => menu.submenus)
      .flatMap((sub) => sub.actions)
      .map((action) => action.code);
    const missingCodes = Array.from(menuCodes).filter(
      (code) => !treeCodes.includes(code),
    );
    const updatedMenus = this.applyCheckedState(tree, menuCodes);
    const checkedCount = updatedMenus
      .flatMap((menu) => menu.submenus)
      .flatMap((sub) => sub.actions)
      .filter((action) => action.checked).length;

    this.menusByApp.update((state) => ({
      ...state,
      [app]: updatedMenus,
    }));
  }

  isSubmenuChecked(sub: SubmenuPermission): boolean {
    return sub.actions.some((action) => action.checked);
  }

  isMenuChecked(menu: MenuGroup): boolean {
    return menu.submenus.some((sub) => this.isSubmenuChecked(sub));
  }

  checkedActions = computed(() => {
    return Object.values(this.menusByApp())
      .flat()
      .flatMap((menu) => menu.submenus)
      .flatMap((sub) => sub.actions)
      .filter((action) => action.checked);
  });

  checkedActionCodes = computed(() => {
    return this.checkedActions().map((action) => action.code);
  });

  /*
     actions
   */

  onSave() {
    this.savePermissions.emit(this.checkedActionCodes());
  }

  toggleAction() {
    if (this.readOnly()) {
      return;
    }

    this.updateActiveMenus((menus) => [...menus]);
  }

  toggleSubmenu(menu: MenuGroup, sub: SubmenuPermission) {
    if (this.readOnly()) {
      return;
    }

    const shouldCheck = !this.isSubmenuChecked(sub);
    sub.actions.forEach((a) => (a.checked = shouldCheck));
    this.updateActiveMenus((menus) => [...menus]);
  }

  toggleMenu(menu: MenuGroup) {
    if (this.readOnly()) {
      return;
    }

    const shouldCheck = !this.isMenuChecked(menu);
    menu.submenus.forEach((sub) =>
      sub.actions.forEach((a) => (a.checked = shouldCheck)),
    );
    this.updateActiveMenus((menus) => [...menus]);
  }

  toggleMenuExpand(menu: MenuGroup) {
    menu.expanded = !menu.expanded;
    this.updateActiveMenus((menus) => [...menus]);
  }

  toggleSubmenuExpand(sub: SubmenuPermission) {
    sub.expanded = !sub.expanded;
    this.updateActiveMenus((menus) => [...menus]);
  }

  /*
     Build menu
   */

  buildMenuTree(data: MenuPermissionDto[]): MenuGroup[] {
    const map = new Map<string, MenuGroup>();
    const permissionCodes = new Set<string>();

    data.forEach((item) => {
      if (!item.menu) return;

      const code = this.buildPermissionCode(item);
      if (code && permissionCodes.has(code)) {
        return;
      }

      if (code) {
        permissionCodes.add(code);
      }

      if (!map.has(item.menu)) {
        map.set(item.menu, {
          name: item.menu,
          expanded: false,
          submenus: [],
        });
      }

      const menu = map.get(item.menu)!;

      let submenu = menu.submenus.find((s) => s.name === item.submenu);

      if (!submenu) {
        submenu = {
          name: item.submenu || '',
          route: item.route || '',
          expanded: false,
          actions: [],
        };
        menu.submenus.push(submenu);
      }

      submenu.actions.push({
        action: item.action || '',
        code,
        checked: false,
        route: item.route ?? '',
      });
    });

    return Array.from(map.values());
  }

  private toMenuCodeSet(
    menuCodes?: Set<string> | string[] | null,
  ): Set<string> {
    if (!menuCodes) {
      return new Set<string>();
    }

    return new Set(
      (Array.isArray(menuCodes) ? menuCodes : Array.from(menuCodes)).filter(
        Boolean,
      ),
    );
  }

  private applyCheckedState(
    menus: MenuGroup[],
    menuCodes: Set<string>,
  ): MenuGroup[] {
    return menus.map((menu) => {
      const submenus = menu.submenus.map((sub) => {
        const actions = sub.actions.map((action) => ({
          ...action,
          checked: menuCodes.has(action.code),
        }));
        const hasCheckedAction = actions.some((action) => action.checked);

        return {
          ...sub,
          expanded: hasCheckedAction,
          actions,
        };
      });
      const hasExpandedSubmenu = submenus.some((sub) => sub.expanded);

      return {
        ...menu,
        expanded: hasExpandedSubmenu,
        submenus,
      };
    });
  }

  private applyCheckedStateByApp(
    menusByApp: MenuState,
    menuCodes: Set<string>,
  ): MenuState {
    return {
      back_office: this.applyCheckedState(menusByApp.back_office, menuCodes),
      pos: this.applyCheckedState(menusByApp.pos, menuCodes),
    };
  }

  private updateActiveMenus(
    updater: (menus: MenuGroup[]) => MenuGroup[],
  ): void {
    const activeApp = this.activeApp();

    this.menusByApp.update((state) => ({
      ...state,
      [activeApp]: updater(state[activeApp]),
    }));
  }

  private buildPermissionCode(item: MenuPermissionDto): string {
    const code = item.code?.trim();

    if (code) {
      return code;
    }

    const menu = item.menu?.trim();
    const submenu = item.submenu?.trim();
    const action = item.action?.trim();

    if (menu && submenu && action) {
      const mappedCode = `${menu}.${submenu}.${action}`;
      return mappedCode;
    }

    return '';
  }

  private assignPosPackageOperationMenu(
    app: PermissionApp,
    data: MenuPermissionDto[],
  ): MenuPermissionDto[] {
    if (app !== 'pos') {
      return data;
    }

    return data.map((item) =>
      POS_PACKAGE_OPERATION_SUBMENUS.has(item.submenu?.toLowerCase() || '')
        ? { ...item, menu: 'pos' }
        : item,
    );
  }

  private readonly normalizedSearchTerm = computed(() =>
    this.searchTerm().trim().toLowerCase(),
  );

  private startLoading(): void {
    this.loadingCount += 1;
    this.loading.set(true);
  }

  private stopLoading(): void {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    this.loading.set(this.loadingCount > 0);
  }

  private filterMenus(menus: MenuGroup[], term: string): MenuGroup[] {
    return menus
      .map((menu) => this.filterMenu(menu, term))
      .filter((menu): menu is MenuGroup => !!menu);
  }

  private filterMenu(menu: MenuGroup, term: string): MenuGroup | null {
    if (this.matchesText(this.getMenuLabel(menu.name), term)) {
      return {
        ...menu,
        expanded: true,
        submenus: menu.submenus.map((sub) => ({
          ...sub,
          expanded: true,
          actions: [...sub.actions],
        })),
      };
    }

    const submenus = menu.submenus
      .map((sub) => this.filterSubmenu(menu, sub, term))
      .filter((sub): sub is SubmenuPermission => !!sub);

    if (submenus.length === 0) {
      return null;
    }

    return {
      ...menu,
      expanded: true,
      submenus,
    };
  }

  private filterSubmenu(
    menu: MenuGroup,
    sub: SubmenuPermission,
    term: string,
  ): SubmenuPermission | null {
    if (
      this.matchesText(this.getSubmenuLabel(sub.name), term) ||
      this.matchesText(sub.route, term)
    ) {
      return {
        ...sub,
        expanded: true,
        actions: [...sub.actions],
      };
    }

    const actions = sub.actions.filter(
      (action) =>
        this.matchesText(this.getActionLabel(action.action), term) ||
        this.matchesText(action.route, term) ||
        this.matchesText(this.getMenuLabel(menu.name), term) ||
        this.matchesText(this.getSubmenuLabel(sub.name), term),
    );

    if (actions.length === 0) {
      return null;
    }

    return {
      ...sub,
      expanded: true,
      actions,
    };
  }

  private matchesText(value: string | null | undefined, term: string): boolean {
    if (!term) {
      return true;
    }

    return (value || '').toLowerCase().includes(term);
  }
}
